import BoldText from '@/components/BoldText';
import CustomCard from '@/components/customCard';
import RegularText from '@/components/RegularText';
import Switch from '@/components/Switch';
import TestEditor from '@/components/TestEditor';
import UpgradeTextWithTouchPoint from '@/components/UpgradeTextWithTouchPoint';
import { BREAKPOINT, CartDateDisplayType, Touchpoint } from '@/constants/enum';
import { optionsSoldOutDisplayType } from '@/constants/options';
import useUserPlan from '@/hooks/useUserPlan';
import { Collapsible, Icon, InlineGrid, RadioButton } from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import { useMediaQuery } from 'react-responsive';

interface IProps {
  handleChangeStatus: (value: boolean) => void;
  handleChangeDisplayType: (value: boolean) => void;
  handleChangeText: (value: string) => void;
  showMethod: boolean;
  soldOutCustomTextEnabled: boolean;
  soldOutText: string;
  customShowError?: boolean;
}

export default function ShowMethodForSoldOut({
  handleChangeStatus,
  showMethod,
  soldOutCustomTextEnabled,
  handleChangeDisplayType,
  handleChangeText,
  soldOutText,
  customShowError,
}: IProps) {
  const { isFreePlan } = useUserPlan();
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT.SM });

  const handleChangeSoldOutDisplayType = (_: boolean, value: boolean) => {
    if (handleChangeDisplayType) {
      handleChangeDisplayType(value);
    }
  };

  return (
    <CustomCard>
      <div className="content-between">
        <div className={`d-flex ${isMobile ? 'flex-column' : 'flex-row align-center'}`}>
          <BoldText className={`bold-text-styled ${isFreePlan ? 'text--gray' : ''}`}>
            Show message for out of stock products
          </BoldText>
        </div>
        {isFreePlan && !showMethod ? (
          isMobile ? null : (
            <div style={{ minWidth: 'fit-content' }}>
              <UpgradeTextWithTouchPoint touchPoint={Touchpoint.SetupOutOfStock} text="Standard or above" />
            </div>
          )
        ) : (
          <Switch
            disabled={isFreePlan && !showMethod}
            isActive={showMethod}
            isLoading={false}
            onSwitch={isFreePlan && !showMethod ? () => {} : handleChangeStatus}
          />
        )}
      </div>
      <RegularText className={`${isFreePlan ? 'text--gray' : ''}`}>
        By default, message text is hidden when products are out of stock. Turn this on to keep it showing or customize message to
        inform restock time, pre-orders, etc.
      </RegularText>
      {isFreePlan && isMobile ? (
        <div>
          <UpgradeTextWithTouchPoint touchPoint={Touchpoint.SetupOutOfStock} text="Standard or above" />
        </div>
      ) : null}
      <Collapsible
        open={showMethod}
        id="basic-collapsible-show-sold-out"
        transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
        expandOnPrint
      >
        <div className="mt-8">
          <InlineGrid columns={2}>
            {optionsSoldOutDisplayType.map((item) => {
              return (
                <RadioButton
                  key={item.value + 'soldout'}
                  label={item.label}
                  checked={
                    (item.value === CartDateDisplayType.CustomizeText && soldOutCustomTextEnabled) ||
                    (item.value === CartDateDisplayType.MessageText && !soldOutCustomTextEnabled)
                  }
                  id={item.value.toString() + 'soldout'}
                  name={item.value.toString() + 'soldout'}
                  onChange={(value) => {
                    handleChangeSoldOutDisplayType(value, item.value === CartDateDisplayType.CustomizeText);
                  }}
                  disabled={!showMethod || isFreePlan}
                />
              );
            })}
          </InlineGrid>
          {soldOutCustomTextEnabled ? (
            <div className="mt-8">
              <div className="mb-16 d-flex">
                <Icon source={AlertCircleIcon} tone="subdued" />
                <RegularText tone="subdued">
                  Date variables are still calculated based on estimates for in-stock products. Enter fixed dates instead.
                </RegularText>
              </div>
              <TestEditor
                isRequired
                disabled={!showMethod || isFreePlan}
                value={soldOutText}
                onBlur={(_, styledValue) => {
                  handleChangeText(styledValue || '');
                }}
                onlyShowEmojiInIconButton
                isHideTipsAndTemplate
                isHideVariableButton
                defaultError={customShowError ? 'This field is required' : ''}
              />
            </div>
          ) : null}
        </div>
      </Collapsible>
    </CustomCard>
  );
}
